/**
 * Mail Center — outbound mail dispatcher.
 *
 * Replaces the read-only mail-job list with an active dispatcher:
 *   1. Outbound queue (filters + per-row send/cancel/track/resend)
 *   2. New mail job modal (single letter OR bureau bundle)
 *   3. Provider status panel (configured pill + in-flight counts)
 *   4. Bulk-send floating bar (appears when 1+ rows selected)
 *
 * Mutations all hit the new /api/mail-jobs/* routes with confirm:true.
 */
import {
  AlertTriangle,
  CheckCheck,
  ExternalLink,
  RotateCcw,
  Send,
  Truck,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "../components/Modal";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import {
  clients as clientsApi,
  disputes as disputesApi,
  letters as lettersApi,
  mail as mailApi,
  type DisputeRound,
  type Letter,
  type MailJob,
  type MailListResponse,
  type MailProviderId,
  type MailProviderInfo,
  request,
} from "../lib/api";

type MailType = "first_class" | "certified";
type Bureau = "experian" | "transunion" | "equifax";

const STATUSES: Array<MailJob["status"] | "all"> = [
  "all",
  "queued",
  "sent",
  "in_transit",
  "staff_mailed",
  "delivered",
  "returned",
  "failed",
  "cancelled",
];

const BUREAU_LABEL: Record<Bureau, string> = {
  experian: "Experian",
  transunion: "TransUnion",
  equifax: "Equifax",
};

function bureauLabel(b: string | null | undefined): string {
  if (!b) return "";
  const key = String(b).toLowerCase() as Bureau;
  return BUREAU_LABEL[key] || String(b);
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function fmtMailType(s: string | null | undefined): string {
  if (!s) return "—";
  return String(s).replace(/_/g, " ");
}

function trackingUrlFor(j: MailJob): string | null {
  const fromRow = j.return_receipt_url || (j.raw_provider_payload as any)?.tracking_url || null;
  if (fromRow) return fromRow;
  const t = j.tracking_number;
  if (!t) return null;
  if (j.provider === "lob") return `https://dashboard.lob.com/letters/${t}`;
  return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(t)}`;
}

// A mailing whose carrier outcome is unknown (the carrier never confirmed),
// or a send that was abandoned mid-way more than 15 minutes ago.
function unresolvedJob(j: any): boolean {
  const p = j?.raw_provider_payload || {};
  if (!["draft", "queued"].includes(j?.status)) return false;
  if (p.ambiguous === true) return true;
  return p.dispatching === true && (!p.reserved_at || Date.now() - Date.parse(p.reserved_at) > 15 * 60_000);
}

export default function MailPage() {
  const [data, setData] = useState<MailListResponse>({
    jobs: [], providers: [], count: 0, limit: 50, offset: 0,
  });
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newJobOpen, setNewJobOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await mailApi.list({
        status: statusFilter === "all" ? undefined : statusFilter,
        provider: providerFilter === "all" ? undefined : providerFilter,
        client_id: clientFilter.trim() || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
      });
      setData(res);
      // Drop selections that are no longer in the visible set.
      setSelected((prev) => {
        const next = new Set<string>();
        const visible = new Set(res.jobs.map((j) => j.id));
        for (const id of prev) if (visible.has(id)) next.add(id);
        return next;
      });
    } catch (e: any) {
      setErr(e?.message || "Could not load mail jobs");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, providerFilter, clientFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  async function resolveJob(j: MailJob, outcome: "sent" | "cancelled") {
    const q = outcome === "sent"
      ? "You checked the carrier dashboard and this mailing WAS printed/sent? Its letters will be recorded as mailed."
      : "You checked the carrier dashboard and this mailing was NOT sent? Its letters go back to Approved and can be mailed again.";
    if (!window.confirm(q)) return;
    setBusy(j.id); setErr(null);
    try {
      await request(`/api/mail-jobs/${j.id}`, { method: "PATCH", body: { status: outcome, confirm: true } });
      await load();
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || "Could not record the outcome");
    } finally { setBusy(null); }
  }

  async function sendOne(j: MailJob) {
    setBusy(j.id);
    setErr(null);
    try {
      await mailApi.send(j.id);
      await load();
    } catch (e: any) {
      const code = e?.body?.error || "";
      const msg = e?.body?.message || e?.message || "Send failed";
      if (code.endsWith("_not_configured")) {
        setErr(`${msg} — switch provider or add the secret.`);
      } else {
        setErr(msg);
      }
    } finally { setBusy(null); }
  }

  async function cancelOne(j: MailJob) {
    if (!window.confirm("Cancel this queued mail job?")) return;
    setBusy(j.id);
    setErr(null);
    try {
      await mailApi.cancel(j.id);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Cancel failed");
    } finally { setBusy(null); }
  }

  async function pollStatus(j: MailJob) {
    setBusy(j.id);
    setErr(null);
    try {
      await mailApi.status(j.id);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Status poll failed");
    } finally { setBusy(null); }
  }

  async function resendFailed(j: MailJob) {
    // Resend = create a new queued job for the same letter + provider + class,
    // then dispatch it.
    setBusy(j.id);
    setErr(null);
    try {
      const mailType: MailType =
        (j.service_type as MailType) === "certified" ? "certified" : "first_class";
      const fresh = await mailApi.queue(j.letter_id, mailType, j.provider);
      await mailApi.send(fresh.id);
      await load();
    } catch (e: any) {
      setErr(e?.message || "Resend failed");
    } finally { setBusy(null); }
  }

  async function bulkSend() {
    if (selected.size === 0) return;
    setBusy("bulk");
    setErr(null);
    try {
      const r = await mailApi.bulkSend(Array.from(selected));
      if (r.failed > 0) {
        const firstErr = r.results.find((x) => !x.ok);
        setErr(`${r.sent} sent · ${r.failed} failed — first error: ${firstErr?.error || firstErr?.code}`);
      }
      setSelected(new Set());
      await load();
    } catch (e: any) {
      setErr(e?.message || "Bulk send failed");
    } finally { setBusy(null); }
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const selectableQueued = useMemo(
    () => data.jobs.filter((j) => j.status === "queued"),
    [data.jobs],
  );
  const allQueuedSelected = selectableQueued.length > 0
    && selectableQueued.every((j) => selected.has(j.id));

  function toggleSelectAllQueued() {
    if (allQueuedSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const j of selectableQueued) next.delete(j.id);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const j of selectableQueued) next.add(j.id);
        return next;
      });
    }
  }

  return (
    <Shell
      title="Letter Mailing"
      subtitle="Dispatch outbound letters by mail"
      actions={
        <button
          className="primary-button"
          onClick={() => setNewJobOpen(true)}
          title="Queue a new mail job"
        >
          <Send size={14} /> New mail job
        </button>
      }
    >
      {/* Provider status panel ----------------------------------------------- */}
      <ProviderStatusPanel providers={data.providers} />

      {/* Outbound queue ------------------------------------------------------ */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-header">
          <div>
            <h2><Send size={18} /> Mail jobs</h2>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ")}</option>
              ))}
            </select>
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              aria-label="Filter by provider"
            >
              <option value="all">All providers</option>
              {data.providers.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Client id"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              style={{ width: 140 }}
              aria-label="Filter by client id"
            />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              title="From date"
              aria-label="From date"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              title="To date"
              aria-label="To date"
            />
            <button className="ghost-button" onClick={load} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>
        {err && <p className="error-text">{err}</p>}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : data.jobs.length === 0 ? (
          <p className="muted small">No mail jobs match these filters.</p>
        ) : (
          <div className="table-list" style={{ marginTop: 8 }}>
            <div
              className="table-row"
              style={{
                background: "var(--ne-silver-4, #f0f0f0)",
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: 11,
                letterSpacing: 0.5,
              }}
            >
              <span style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allQueuedSelected}
                  onChange={toggleSelectAllQueued}
                  disabled={selectableQueued.length === 0}
                  title="Select all queued"
                />
              </span>
              <span>Job</span>
              <span>Client</span>
              <span>Type</span>
              <span>Provider</span>
              <span>Class</span>
              <span>Status</span>
              <span>Created</span>
              <span style={{ textAlign: "right" }}>Actions</span>
            </div>
            {data.jobs.map((j) => {
              const url = trackingUrlFor(j);
              const isSelected = selected.has(j.id);
              const isBundle = (j.raw_provider_payload as any)?.bundle === true;
              return (
                <div className="table-row" key={j.id}>
                  <span style={{ width: 32 }}>
                    {j.status === "queued" && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelected(j.id)}
                      />
                    )}
                  </span>
                  <span style={{ fontFamily: "monospace" }}>{j.id.slice(0, 8)}</span>
                  <strong>{j.client_name || j.client_id?.slice(0, 8) || "—"}</strong>
                  <span>
                    {isBundle
                      ? `Bundle · ${bureauLabel((j.raw_provider_payload as any)?.recipient_bureau)}`
                      : (j.letter_kind ? j.letter_kind.replace(/_/g, " ") : "Letter")}
                    {j.recipient_bureau && !isBundle && (
                      <span className="muted small" style={{ marginLeft: 6 }}>
                        → {bureauLabel(j.recipient_bureau)}
                      </span>
                    )}
                  </span>
                  <span style={{ textTransform: "uppercase", fontSize: 11 }}>{j.provider}</span>
                  <span>{fmtMailType(j.service_type)}</span>
                  <StatusPill status={unresolvedJob(j) ? "outcome unknown" : (j.raw_provider_payload as any)?.dispatching ? "sending" : j.status} />
                  <span className="muted small">{fmtDate(j.created_at)}</span>
                  <span style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {unresolvedJob(j) && (
                      <>
                        <button className="primary-button" disabled={busy === j.id} onClick={() => resolveJob(j, "sent")}
                          title="You checked the carrier dashboard and this was printed/mailed">
                          Recorded as sent
                        </button>
                        <button className="ghost-button" disabled={busy === j.id} onClick={() => resolveJob(j, "cancelled")}
                          title="You checked the carrier dashboard and this was NOT mailed">
                          Confirmed not sent
                        </button>
                      </>
                    )}
                    {j.status === "queued" && !unresolvedJob(j) && !(j.raw_provider_payload as any)?.dispatching && (
                      <>
                        <button
                          className="primary-button"
                          disabled={busy === j.id}
                          onClick={() => sendOne(j)}
                          title="Dispatch this job to its provider"
                        >
                          <Send size={12} /> Send
                        </button>
                        <button
                          className="ghost-button"
                          disabled={busy === j.id}
                          onClick={() => cancelOne(j)}
                          title="Cancel this queued job"
                        >
                          <X size={12} /> Cancel
                        </button>
                      </>
                    )}
                    {(j.status === "sent" || j.status === "in_transit") && (
                      <button
                        className="ghost-button"
                        disabled={busy === j.id}
                        onClick={() => pollStatus(j)}
                        title="Poll provider for updated status"
                      >
                        <RotateCcw size={12} /> Poll
                      </button>
                    )}
                    {url && (
                      <a
                        className="ghost-button"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open tracking URL"
                      >
                        {j.provider === "lob"
                          ? (<><ExternalLink size={12} /> Lob letter</>)
                          : (<><Truck size={12} /> Track</>)}
                      </a>
                    )}
                    {j.status === "failed" && (
                      <button
                        className="ghost-button"
                        disabled={busy === j.id}
                        onClick={() => resendFailed(j)}
                        title="Queue and dispatch a fresh copy of this letter"
                      >
                        <RotateCcw size={12} /> Resend
                      </button>
                    )}
                    {j.status === "staff_mailed" && (
                      <button
                        className="ghost-button"
                        disabled={busy === j.id}
                        onClick={async () => {
                          setBusy(j.id);
                          setErr(null);
                          try {
                            // Manual jobs progress via PATCH delivered.
                            const r = await fetch(`/api/mail-jobs/${j.id}`, {
                              method: "PATCH",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ status: "delivered", delivered_at: new Date().toISOString(), confirm: true }),
                              credentials: "include",
                            });
                            if (!r.ok) throw new Error(`HTTP ${r.status}`);
                            await load();
                          } catch (e: any) {
                            setErr(e?.message || "Mark delivered failed");
                          } finally { setBusy(null); }
                        }}
                      >
                        <CheckCheck size={12} /> Mark delivered
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bulk-send floating bar --------------------------------------------- */}
      {selected.size > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--ne-black, #0a0a0a)",
            color: "#fff",
            padding: "12px 20px",
            borderRadius: 8,
            display: "flex",
            gap: 14,
            alignItems: "center",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            zIndex: 100,
            border: "1px solid var(--ne-silver-2, #c0c0c0)",
          }}
        >
          <strong style={{ color: "#fff" }}>{selected.size} selected</strong>
          <button
            className="primary-button"
            onClick={bulkSend}
            disabled={busy === "bulk"}
          >
            <Send size={14} /> {busy === "bulk" ? "Sending…" : "Send all"}
          </button>
          <button
            className="ghost-button"
            onClick={() => setSelected(new Set())}
            style={{ color: "#fff", borderColor: "var(--ne-silver-2, #c0c0c0)" }}
          >
            Clear
          </button>
        </div>
      )}

      {/* New-job modal ------------------------------------------------------ */}
      <NewMailJobModal
        open={newJobOpen}
        providers={data.providers}
        onClose={() => setNewJobOpen(false)}
        onCreated={() => { setNewJobOpen(false); load(); }}
      />
    </Shell>
  );
}

/* -------------------------------------------------------------------------- */
/* Provider status panel                                                       */
/* -------------------------------------------------------------------------- */

function ProviderStatusPanel({ providers }: { providers: MailProviderInfo[] }) {
  if (providers.length === 0) return null;
  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h2><Truck size={18} /> Mail provider status</h2>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          marginTop: 8,
        }}
      >
        {providers.map((p) => (
          <div
            key={p.id}
            className="sub-panel"
            style={{
              padding: 12,
              border: "1px solid var(--ne-line-2)",
              borderRadius: 6,
              background: "var(--ne-surface-2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <strong>{p.id === "manual" ? "Print & mail yourself" : p.name}</strong>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "3px 8px",
                  borderRadius: 999,
                  textTransform: "uppercase",
                  background: p.configured ? "#0a8a3f" : "#3a3a3f",
                  color: "#fff",
                }}
              >
                {p.configured ? "Ready" : "Optional"}
              </span>
            </div>
            <div className="muted small">
              <div>{p.supported_mail_types.join(" · ")}</div>
              <div style={{ marginTop: 2 }}>
                {p.inflight} in flight
              </div>
              {p.id === "manual" && (
                <div style={{ marginTop: 6 }}>Always available — no account needed. Download the bundle, print, drop at the post office, mark delivered.</div>
              )}
              {!p.configured && p.id !== "manual" && (
                <div style={{ display: "flex", gap: 4, alignItems: "center", marginTop: 6 }}>
                  <span>Connect your {p.name} account for one-click automatic mailing →</span>
                  <a href="/settings" style={{ color: "var(--ne-red)", textDecoration: "underline" }}>Settings</a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* New mail job modal                                                          */
/* -------------------------------------------------------------------------- */

function NewMailJobModal({
  open,
  providers,
  onClose,
  onCreated,
}: {
  open: boolean;
  providers: MailProviderInfo[];
  onClose: () => void;
  onCreated: () => void;
}) {
  type Source = "letter" | "bundle";
  const [source, setSource] = useState<Source>("letter");
  const [provider, setProvider] = useState<MailProviderId | "">("");
  const [mailType, setMailType] = useState<MailType>("first_class");
  // letter source state
  const [clientId, setClientId] = useState("");
  const [letters, setLetters] = useState<Letter[]>([]);
  const [letterId, setLetterId] = useState("");
  const [loadingLetters, setLoadingLetters] = useState(false);
  // bundle source state
  const [rounds, setRounds] = useState<DisputeRound[]>([]);
  const [roundId, setRoundId] = useState("");
  const [bureau, setBureau] = useState<Bureau>("experian");
  const [loadingRounds, setLoadingRounds] = useState(false);
  // misc
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    // Reset state on each open
    setProvider(""); // no default carrier — the operator chooses
    setMailType("first_class");
    setSource("letter");
    setClientId("");
    setLetters([]);
    setLetterId("");
    setRounds([]);
    setRoundId("");
    setBureau("experian");
    setErr(null);
    setResult(null);
  }, [open, providers]);

  // Load letters when client id changes (letter source).
  useEffect(() => {
    if (source !== "letter" || !clientId.trim()) { setLetters([]); return; }
    let cancelled = false;
    setLoadingLetters(true);
    lettersApi
      .list({ client_id: clientId.trim() })
      .then((ls) => { if (!cancelled) setLetters(ls); })
      .catch(() => { if (!cancelled) setLetters([]); })
      .finally(() => { if (!cancelled) setLoadingLetters(false); });
    return () => { cancelled = true; };
  }, [source, clientId]);

  // Load rounds when client id changes (bundle source).
  useEffect(() => {
    if (source !== "bundle" || !clientId.trim()) { setRounds([]); return; }
    let cancelled = false;
    setLoadingRounds(true);
    disputesApi
      .list({ client_id: clientId.trim() })
      .then((rs) => { if (!cancelled) setRounds(rs); })
      .catch(() => { if (!cancelled) setRounds([]); })
      .finally(() => { if (!cancelled) setLoadingRounds(false); });
    return () => { cancelled = true; };
  }, [source, clientId]);

  async function submit() {
    setSubmitting(true);
    setErr(null);
    setResult(null);
    try {
      if (!provider) throw new Error("Pick a mail carrier");
      if (source === "letter") {
        if (!letterId) throw new Error("Pick a letter");
        const job = await mailApi.queue(letterId, mailType, provider);
        setResult(`Queued mail job ${job.id.slice(0, 8)} · ${job.provider}`);
        onCreated();
      } else {
        if (!roundId) throw new Error("Pick a round");
        const r = await mailApi.sendBundle({
          round_id: roundId, bureau, provider, mail_type: mailType,
        });
        setResult(`Bundle sent · mail_job ${r.mail_job_id.slice(0, 8)} · status ${r.status}`);
        onCreated();
      }
    } catch (e: any) {
      const code = e?.body?.error || "";
      const msg = e?.body?.message || e?.message || "Submit failed";
      if (code.endsWith("_not_configured")) {
        setErr(`${msg} — switch provider.`);
      } else {
        setErr(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title="New mail job"
      onClose={onClose}
      wide
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="ghost-button" disabled={submitting} onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" disabled={submitting || !provider} onClick={submit}>
            {submitting
              ? "Submitting…"
              : source === "letter" ? "Confirm and queue" : "Confirm and send bundle"}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <span className="field-label">Source</span>
          <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                checked={source === "letter"}
                onChange={() => setSource("letter")}
              />
              Single letter
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                checked={source === "bundle"}
                onChange={() => setSource("bundle")}
              />
              Bureau bundle
            </label>
          </div>
          <p className="muted small" style={{ marginTop: 4 }}>
            Letters → queued (you send from the list). Bundles → render + dispatch immediately.
          </p>
        </div>

        <div>
          <span className="field-label">Client id</span>
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="uuid of the client"
            style={{ width: "100%" }}
          />
        </div>

        {source === "letter" && (
          <div>
            <span className="field-label">Letter</span>
            {!clientId.trim() ? (
              <p className="muted small">Enter a client id to load letters.</p>
            ) : loadingLetters ? (
              <p className="muted">Loading…</p>
            ) : letters.length === 0 ? (
              <p className="muted small">No letters for this client.</p>
            ) : (
              <select
                value={letterId}
                onChange={(e) => setLetterId(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">— pick a letter —</option>
                {letters.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.id.slice(0, 8)} · {l.kind} · {bureauLabel((l as any).recipient_bureau)} · {l.status}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {source === "bundle" && (
          <>
            <div>
              <span className="field-label">Dispute round</span>
              {!clientId.trim() ? (
                <p className="muted small">Enter a client id to load rounds.</p>
              ) : loadingRounds ? (
                <p className="muted">Loading…</p>
              ) : rounds.length === 0 ? (
                <p className="muted small">No rounds for this client.</p>
              ) : (
                <select
                  value={roundId}
                  onChange={(e) => setRoundId(e.target.value)}
                  style={{ width: "100%" }}
                >
                  <option value="">— pick a round —</option>
                  {rounds.map((r) => (
                    <option key={r.id} value={r.id}>
                      Round {r.round_number} · {r.status} · {r.id.slice(0, 8)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <span className="field-label">Bureau</span>
              <div style={{ display: "flex", gap: 14, marginTop: 6 }}>
                {(Object.keys(BUREAU_LABEL) as Bureau[]).map((b) => (
                  <label key={b} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="radio"
                      checked={bureau === b}
                      onChange={() => setBureau(b)}
                    />
                    {BUREAU_LABEL[b]}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <div>
          <span className="field-label">Provider</span>
          <div style={{ display: "flex", gap: 14, marginTop: 6, flexWrap: "wrap" }}>
            {providers.map((p) => (
              <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="radio"
                  name="provider"
                  checked={provider === p.id}
                  disabled={!p.configured}
                  onChange={() => setProvider(p.id)}
                />
                {p.name}{!p.configured && <span className="muted small"> (unconfigured)</span>}
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className="field-label">Mail class</span>
          <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                checked={mailType === "first_class"}
                onChange={() => setMailType("first_class")}
              />
              First-Class
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                checked={mailType === "certified"}
                onChange={() => setMailType("certified")}
              />
              Certified
            </label>
          </div>
        </div>

        <p className="muted small">
          {source === "letter"
            ? "Mail job is queued, not sent. Dispatch from the Outbound queue."
            : "Bureau bundle is rendered and dispatched to the provider in one step."}
        </p>

        {err && <p className="error-text">{err}</p>}
        {result && <p className="muted small" style={{ color: "var(--ne-red-dark, #8c1d18)" }}>{result}</p>}
      </div>
    </Modal>
  );
}

// Avoid unused-import warning if the AlertTriangle/clientsApi aren't used at
// runtime depending on conditional renders — keep them touched.
void clientsApi;
